import React, { useState, useRef } from "react";
import { 
  XMarkIcon, 
  FolderIcon, 
  ArrowUpTrayIcon,
  CheckCircleIcon,
  ExclamationCircleIcon,
} from "@heroicons/react/24/outline";
import { 
  useBulkImportSingleAidaForm, 
  useCheckCustomersBatch 
} from "../../hooks/useAmazonClaims";
import toast from "react-hot-toast";

interface BulkImportModalProps {
  isOpen: boolean;
  onClose: () => void;
}

interface AidaFormResponse {
  id: string; // The aidaForm response ID
  email?: string;
  accountEmail: string;
  firstName?: string;
  lastName?: string;
  name?: string;
  submittedAt?: string;
  createdAtReadable?: string;
  folderName?: string;
  hasPdf?: boolean;
  customerId?: string;
  customerDisplayId?: string;
  exists?: boolean;
  userType?: 'customer' | 'dealer';
}

const BulkImportModal: React.FC<BulkImportModalProps> = ({ isOpen, onClose }) => {
  const [files, setFiles] = useState<File[]>([]);
  const [responses, setResponses] = useState<AidaFormResponse[]>([]);
  const [cashbackRate, setCashbackRate] = useState<number>(5.5);
  const [isProcessing, setIsProcessing] = useState(false);
  const [progress, setProgress] = useState({ current: 0, total: 0 });
  const [results, setResults] = useState<{ id: string; success: boolean; message?: string }[]>([]);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [isChecking, setIsChecking] = useState(false);
  
  const fileInputRef = useRef<HTMLInputElement>(null);
  const importMutation = useBulkImportSingleAidaForm();
  const checkCustomersMutation = useCheckCustomersBatch();

  if (!isOpen) return null;

  const handleFolderSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFiles = Array.from(e.target.files || []);
    if (selectedFiles.length === 0) return;

    setFiles(selectedFiles);
    
    // Look for combined_responses.json or info.json
    const rootInfoFile = selectedFiles.find(f => 
      ['info.json', 'combined_responses.json'].includes(f.name.toLowerCase()) && 
      !f.webkitRelativePath.includes('/') // Simplified: root of the folder they selected
    );
    
    // Fallback: If no root array, look for all info.json in subfolders
    const infoFiles = selectedFiles.filter(f => f.name.toLowerCase() === 'info.json');

    try {
      let data: any[] = [];
      
      if (rootInfoFile) {
        const text = await rootInfoFile.text();
        const parsed = JSON.parse(text);
        data = Array.isArray(parsed) ? parsed : [parsed];
      } else if (infoFiles.length > 0) {
        // Aggregate all info.json from subfolders
        const parsedResults = await Promise.all(
          infoFiles.map(async (f) => {
            try {
              const text = await f.text();
              const parsed = JSON.parse(text);
              const folderPrefix = f.webkitRelativePath.replace('info.json', '');
              const associatedFiles = selectedFiles.filter(sf => sf.webkitRelativePath.startsWith(folderPrefix));
              const folderMatch = f.webkitRelativePath.match(/response_([^/]+)/);
              return { 
                ...parsed, 
                folderName: folderMatch ? folderMatch[0] : `response_${parsed.id}`,
                hasPdf: associatedFiles.some(af => af.name.toLowerCase().endsWith('.pdf'))
              };
            } catch { return null; }
          })
        );
        data = parsedResults.filter(r => r !== null);
      }

      if (data.length > 0) {
        setResponses(data);
        setSelectedIds(data.map(r => r.id));
        setProgress({ current: 0, total: data.length });
      } else {
        toast.error("No valid response data found in selection");
      }
    } catch (error) {
      toast.error("Failed to parse response data");
    }
  };

  const readFileAsBase64 = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const base64 = reader.result as string;
        // Remove data:image/xxx;base64, prefix
        const base64Data = base64.split(',')[1];
        resolve(base64Data);
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  };
  
  const toggleSelect = (id: string) => {
    setSelectedIds(prev => 
      prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
    );
  };

  const toggleSelectAll = () => {
    if (selectedIds.length === responses.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(responses.map(r => r.id));
    }
  };

  const handleCheckCustomers = async () => {
    if (responses.length === 0) return;
    
    setIsChecking(true);
    try {
      const emails = responses.map(r => r.accountEmail);
      const resultsMap = await checkCustomersMutation.mutateAsync(emails);

      setResponses(prev => prev.map(res => {
        const checkResult = resultsMap[res.accountEmail.trim().toLowerCase()];
        if (checkResult) {
          return {
            ...res,
            customerId: checkResult.id || undefined,
            customerDisplayId: checkResult.displayId || undefined,
            exists: checkResult.exists,
            userType: checkResult.type as any
          };
        }
        return res;
      }));
      
      toast.success("Customer check completed");
    } catch (error) {
      toast.error("Failed to check customers");
    } finally {
      setIsChecking(false);
    }
  };

  const exportToCsv = () => {
    if (responses.some(r => r.exists === undefined)) {
      if (!window.confirm("You haven't checked all customers yet. Export anyway?")) {
        return;
      }
    }
    
    const headers = ["AidaForm ID", "Account Email", "Format", "Status", "User Type", "isCustomer", "Customer ID"];
    const rows = responses.map(r => [
      r.id,
      r.accountEmail,
      r.hasPdf ? "PDF" : "IMG",
      r.exists === undefined ? "Unknown" : (r.exists ? "Registered" : "Not Found"),
      r.userType || "",
      r.userType === 'customer' ? "YES" : (r.userType === 'dealer' ? "NO" : ""),
      r.customerId || ""
    ]);
    
    const csvContent = [
      headers.join(","),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(","))
    ].join("\n");
    
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.setAttribute("href", url);
    link.setAttribute("download", `aidaform_import_audit_${new Date().toISOString().split('T')[0]}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const startImport = async () => {
    const toProcess = responses.filter(r => selectedIds.includes(r.id));
    if (toProcess.length === 0) {
      toast.error("Please select at least one response to import");
      return;
    }
    
    setIsProcessing(true);
    setResults([]);
    let processedCount = 0;
    setProgress({ current: 0, total: toProcess.length });

    for (const res of toProcess) {
      try {
        // Find screenshots in the files array
        const folderPrefix = `${res.folderName}/`;
        const screenshotFiles = files.filter(f => 
          f.webkitRelativePath.includes(folderPrefix) && 
          /\.(jpg|jpeg|png|webp|pdf)$/i.test(f.name)
        );

        if (screenshotFiles.length === 0) {
          setResults(prev => [...prev, { id: res.id, success: false, message: "No screenshots found" }]);
          processedCount++;
          const skippedCurrent = processedCount;
          setProgress(p => ({ ...p, current: skippedCurrent }));
          continue;
        }

        // Convert images to base64
        const screenshotsBase64 = await Promise.all(
          screenshotFiles.map(f => readFileAsBase64(f))
        );

        // Call backend
        await importMutation.mutateAsync({
          email: res.accountEmail,
          aidaFormId: res.id,
          screenshots: screenshotsBase64,
          cashbackRate,
        });

        setResults(prev => [...prev, { id: res.id, success: true }]);
      } catch (error: any) {
        const errorMsg = error.response?.data?.message || error.message || "Unknown error";
        setResults(prev => [...prev, { id: res.id, success: false, message: errorMsg }]);
      }

      processedCount++;
      const current = processedCount;
      setProgress(p => ({ ...p, current }));
    }

    setIsProcessing(false);
    toast.success("Bulk import process completed");
  };

  return (
    <div className="fixed inset-0 z-[60] overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        <div className="fixed inset-0 transition-opacity" onClick={() => !isProcessing && onClose()}>
          <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
        </div>

        <div className="inline-block align-bottom bg-white dark:bg-gray-800 rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-2xl sm:w-full">
          <div className="px-6 py-4 bg-primary-600 flex items-center justify-between">
            <h3 className="text-xl font-semibold text-white">Bulk Import</h3>
            <button 
              onClick={onClose} 
              disabled={isProcessing}
              className="text-white hover:text-gray-200 disabled:opacity-50"
            >
              <XMarkIcon className="h-6 w-6" />
            </button>
          </div>

          <div className="p-6 space-y-6">
            {!isProcessing && results.length === 0 ? (
              <>
                <div className="space-y-4">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    Step 1: Select Export Folder
                  </label>
                  <div 
                    onClick={() => fileInputRef.current?.click()}
                    className="mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-gray-300 dark:border-gray-600 border-dashed rounded-md cursor-pointer hover:border-primary-500 transition-colors"
                  >
                    <div className="space-y-1 text-center">
                      <FolderIcon className="mx-auto h-12 w-12 text-gray-400" />
                      <div className="flex text-sm text-gray-600 dark:text-gray-400">
                        <span className="relative cursor-pointer bg-white dark:bg-gray-800 rounded-md font-medium text-primary-600 hover:text-primary-500">
                          {files.length > 0 ? `${files.length} files selected` : "Select folder"}
                        </span>
                      </div>
                      <p className="text-xs text-gray-500">
                        Select the top-level folder containing info.json
                      </p>
                    </div>
                  </div>
                  <input
                    type="file"
                    ref={fileInputRef}
                    onChange={handleFolderSelect}
                    className="hidden"
                    // @ts-ignore
                    webkitdirectory="true"
                    multiple
                  />
                </div>

                {responses.length > 0 && (
                  <div className="space-y-4">
                    <div className="bg-blue-50 dark:bg-blue-900/20 p-3 rounded-md text-xs text-blue-700 dark:text-blue-300 flex justify-between items-center">
                       <span>Found <strong>{responses.length}</strong> responses in selection.</span>
                       <div className="flex space-x-2">
                          <button 
                            onClick={handleCheckCustomers} 
                            disabled={isChecking}
                            className="bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300 px-2 py-1 rounded hover:bg-primary-200"
                          >
                            {isChecking ? "Checking..." : "Check Customers"}
                          </button>
                          <button 
                            onClick={exportToCsv}
                            className="bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 px-2 py-1 rounded hover:bg-gray-200"
                          >
                            Export CSV
                          </button>
                       </div>
                    </div>

                    <div className="max-h-48 overflow-y-auto border border-gray-200 dark:border-gray-700 rounded-md">
                      <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                        <thead className="bg-gray-50 dark:bg-gray-900 sticky top-0">
                          <tr>
                            <th className="px-4 py-2 text-left">
                              <input 
                                type="checkbox" 
                                checked={selectedIds.length === responses.length && responses.length > 0}
                                onChange={toggleSelectAll}
                                className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                              />
                            </th>
                            <th className="px-4 py-2 text-left text-[10px] font-medium text-gray-500 uppercase tracking-wider">Account Email</th>
                            <th className="px-4 py-2 text-left text-[10px] font-medium text-gray-500 uppercase tracking-wider">Status</th>
                            <th className="px-4 py-2 text-left text-[10px] font-medium text-gray-500 uppercase tracking-wider">Format</th>
                          </tr>
                        </thead>
                        <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                          {responses.map((res) => (
                            <tr key={res.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                              <td className="px-4 py-2">
                                <input 
                                  type="checkbox" 
                                  checked={selectedIds.includes(res.id)}
                                  onChange={() => toggleSelect(res.id)}
                                  className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                                />
                              </td>
                               <td className="px-4 py-2 text-xs truncate max-w-[180px]">{res.accountEmail}</td>
                              <td className="px-4 py-2">
                                {res.exists === undefined ? (
                                  <span className="text-[10px] text-gray-400 italic">Not checked</span>
                                ) : res.exists ? (
                                  <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium ${
                                    res.userType === 'dealer' 
                                      ? "bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-400" 
                                      : "bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400"
                                  }`}>
                                    {res.userType === 'dealer' ? 'Dealer: ' : 'Found: '}{res.customerDisplayId}
                                  </span>
                                ) : (
                                  <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-400">
                                    Not Found
                                  </span>
                                )}
                              </td>
                              <td className="px-4 py-2">
                                {res.hasPdf ? (
                                  <span className="inline-flex items-center px-2 py-0.5 rounded text-[10px] font-medium bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400">
                                    PDF
                                  </span>
                                ) : (
                                  <span className="inline-flex items-center px-2 py-0.5 rounded text-[10px] font-medium bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400">
                                    IMG
                                  </span>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                        Step 2: Custom Cashback Rate (%)
                      </label>
                      <input
                        type="number"
                        value={cashbackRate}
                        onChange={(e) => setCashbackRate(parseFloat(e.target.value))}
                        className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-primary-500 focus:ring-primary-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                        step="0.1"
                      />
                    </div>

                    <button
                      onClick={startImport}
                      disabled={selectedIds.length === 0}
                      className="w-full inline-flex justify-center items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50"
                    >
                      <ArrowUpTrayIcon className="h-4 w-4 mr-2" />
                      Start Upload ({selectedIds.length})
                    </button>
                  </div>
                )}
              </>
            ) : (
              <div className="space-y-4">
                <div className="flex items-center justify-between text-sm font-medium">
                  <span className="text-gray-700 dark:text-gray-300">
                    {isProcessing ? "Processing..." : "Import Completed"}
                  </span>
                  <span className="text-primary-600">
                    {progress.current} / {progress.total}
                  </span>
                </div>
                <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2.5">
                  <div 
                    className="bg-primary-600 h-2.5 rounded-full transition-all duration-300" 
                    style={{ width: `${(progress.current / progress.total) * 100}%` }}
                  ></div>
                </div>

                <div className="max-h-60 overflow-y-auto border border-gray-200 dark:border-gray-700 rounded-md">
                  <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                    <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                      {responses
                        .filter(res => results.some(r => r.id === res.id) || (isProcessing && selectedIds.includes(res.id)))
                        .map((res, i) => {
                          const result = results.find(r => r.id === res.id);
                        return (
                          <tr key={res.id || i}>
                            <td className="px-4 py-2 text-xs truncate max-w-[150px]">{res.accountEmail}</td>
                            <td className="px-4 py-2 text-right">
                              {result ? (
                                result.success ? (
                                  <CheckCircleIcon className="h-5 w-5 text-green-500 ml-auto" />
                                ) : (
                                  <div className="flex items-center justify-end group relative">
                                    <ExclamationCircleIcon className="h-5 w-5 text-red-500 cursor-help" />
                                    <div className="hidden group-hover:block absolute bottom-full right-0 mb-2 p-2 bg-red-600 text-white text-[10px] rounded shadow-lg z-10 w-48">
                                      {result.message}
                                    </div>
                                  </div>
                                )
                              ) : (
                                <div className="animate-pulse h-2 w-12 bg-gray-200 dark:bg-gray-700 rounded ml-auto"></div>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

                {!isProcessing && (
                  <button
                    onClick={onClose}
                    className="w-full inline-flex justify-center items-center px-4 py-2 border border-gray-300 dark:border-gray-600 text-sm font-medium rounded-md text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600"
                  >
                    Close
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default BulkImportModal;
